// The one confirmation UX: every input method (text/voice, photo, barcode,
// search, favorites) funnels into this editable list. User adjusts grams/ml +
// meal inline, then saves. Mirrors brief §7.
import { useState } from "preact/hooks";
import { api, candidateMacros } from "../api";
import type { FoodCandidate, LogEntryInput, Meal } from "@hh/shared";

const MEALS: Meal[] = ["breakfast", "lunch", "dinner", "snack"];
const UNITS = ["g", "ml", "serving"] as const;

export function CandidateList({
  candidates,
  inputMethod,
  onSaved,
  onClear,
}: {
  candidates: FoodCandidate[];
  inputMethod: LogEntryInput["input_method"];
  onSaved: () => void;
  onClear: () => void;
}) {
  const [items, setItems] = useState<FoodCandidate[]>(candidates);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function update(i: number, patch: Partial<FoodCandidate>) {
    setItems((xs) => xs.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  }
  function remove(i: number) {
    setItems((xs) => xs.filter((_, idx) => idx !== i));
  }

  async function save() {
    setBusy(true);
    setErr("");
    try {
      const entries: LogEntryInput[] = items.map((c) => ({
        food_id: c.food_id ?? undefined,
        food: c.food_id
          ? undefined
          : {
              source: c.source,
              barcode: c.barcode,
              name: c.name,
              brand: c.brand,
              per100_kcal: c.per100_kcal,
              per100_protein: c.per100_protein,
              per100_carbs: c.per100_carbs,
              per100_fat: c.per100_fat,
              per100_fiber: c.per100_fiber,
              default_serving: c.default_serving,
              serving_unit: c.serving_unit,
            },
        amount: c.amount,
        unit: c.unit,
        meal: c.meal,
        input_method: inputMethod,
      }));
      await api.logFood(entries);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  if (!items.length) {
    return (
      <div class="empty">
        <p>Nothing to confirm.</p>
        <button class="btn secondary" onClick={onClear}>Back</button>
      </div>
    );
  }

  return (
    <div class="fade-in">
      {items.map((c, i) => {
        const m = candidateMacros(c);
        return (
          <div class="cand" key={i}>
            <div class="top">
              <div>
                <div class="nm">{c.name}</div>
                {c.brand && <div class="br">{c.brand}</div>}
              </div>
              <button class="rm" onClick={() => remove(i)} aria-label="Remove">×</button>
            </div>

            <div class="ctl">
              <input
                type="number" inputMode="decimal" value={c.amount}
                onInput={(e) => update(i, { amount: +(e.target as HTMLInputElement).value })}
              />
              <select value={c.unit}
                onChange={(e) => update(i, { unit: (e.target as HTMLSelectElement).value as FoodCandidate["unit"] })}>
                {UNITS.map((u) => <option value={u} key={u}>{u}</option>)}
              </select>
              <select value={c.meal}
                onChange={(e) => update(i, { meal: (e.target as HTMLSelectElement).value as Meal })}>
                {MEALS.map((mm) => <option value={mm} key={mm}>{mm}</option>)}
              </select>
              <span class={`conf ${c.confidence}`}>{c.confidence}</span>
            </div>

            <div class="macros">
              <span><b>{m.kcal}</b> kcal</span>
              <span><b>{m.protein_g}</b>P</span>
              <span><b>{m.carbs_g}</b>C</span>
              <span><b>{m.fat_g}</b>F</span>
            </div>
            {c.note && <div class="note">{c.note}</div>}
          </div>
        );
      })}

      {err && <div class="err">{err}</div>}
      <button class="btn" onClick={save} disabled={busy}>
        {busy ? <span class="spinner" /> : `Log ${items.length} item${items.length > 1 ? "s" : ""}`}
      </button>
      <button class="btn ghost" onClick={onClear} disabled={busy}>Cancel</button>
    </div>
  );
}
